import axios from "axios";
import UserInfo from "./UserInfo";

const API_BASE_URL = "/api/v1";

// Token storage keys
const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_INFO_KEY = "user_info";
const TOKEN_EXPIRES_KEY = "token_expires_in";
const REDIRECT_URL_KEY = "redirect_url";
const LANGUAGE_LIST = "language_list";

/**
 * Get stored access token
 */
export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Get stored refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Get stored user info
 */
export const getUserInfo = () => {
  const userInfo = localStorage.getItem(USER_INFO_KEY);
  return userInfo ? JSON.parse(userInfo) : null;
};

/**
 * Store authentication tokens
 */
export const setTokens = (accessToken, refreshToken, expiresIn, userInfo) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(TOKEN_EXPIRES_KEY, expiresIn);
  if (userInfo) {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  }
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_KEY);
  localStorage.removeItem(LANGUAGE_LIST);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

/**
 * Decode a JWT's payload locally (no signature verification, no network
 * call) to read its `exp` claim.
 */
const decodeJwtPayload = (token) => {
  try {
    const base64Payload = token.split(".")[1];
    const json = atob(base64Payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

/**
 * Check if a JWT access token is expired, purely client-side. Treats
 * missing/malformed tokens as expired.
 */
export const isAccessTokenExpired = (token) => {
  const payload = token && decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }
  return payload.exp * 1000 <= Date.now();
};

/**
 * Save the current URL for redirect after login
 */
export const saveRedirectUrl = () => {
  const currentUrl = window.location.pathname + window.location.search;
  localStorage.setItem(REDIRECT_URL_KEY, currentUrl);
};

/**
 * Get and clear the saved redirect URL
 */
export const getRedirectUrl = () => {
  const redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);
  localStorage.removeItem(REDIRECT_URL_KEY);
  return redirectUrl || "/";
};

// Single-flight guard so concurrent callers share one /auth/refresh request.
let refreshPromise = null;

/**
 * Refresh the access token using refresh token
 */
export const refreshAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token available"));
  }

  refreshPromise = axios
    .post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken,
      grant_type: "refresh_token",
    })
    .then((response) => {
      const { access_token, refresh_token, expires_in, user } = response.data;
      const existingUser = getUserInfo() || {};
      const mergedUser = user ? { ...existingUser, ...user } : existingUser;
      setTokens(access_token, refresh_token, expires_in, mergedUser);
      return access_token;
    })
    .catch((error) => {
      saveRedirectUrl();
      clearAuth();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Logout - revoke tokens and clear storage
 */
export const logout = async () => {
  const refreshToken = getRefreshToken();

  if (refreshToken) {
    try {
      await axios.post(`${API_BASE_URL}/auth/revoke`, {
        refresh_token: refreshToken,
      });
    } catch (error) {
      // Ignore errors during logout
    }
  }

  saveRedirectUrl();
  clearAuth();
  window.location.href = "/login";
};

/**
 * Setup axios interceptors for automatic token handling
 */
export const setupAxiosInterceptors = () => {
  // Request interceptor - add Authorization header
  axios.interceptors.request.use(
    (config) => {
      const token = getAccessToken();

      // Only add token for API requests (not for login/refresh endpoints)
      if (
        token &&
        config.url &&
        !config.url.includes("/auth/login") &&
        !config.url.includes("/auth/refresh")
      ) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle token refresh on 401
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Skip if not a 401 or if it's an auth endpoint
      if (
        error.response?.status !== 401 ||
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/refresh")
      ) {
        return Promise.reject(error);
      }

      // If already retried this request, reject
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Shared single-flight refresh.
        const accessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }
  );
};

export default {
  getAccessToken,
  getRefreshToken,
  getUserInfo,
  setTokens,
  clearAuth,
  isAuthenticated,
  isAccessTokenExpired,
  saveRedirectUrl,
  getRedirectUrl,
  refreshAccessToken,
  logout,
  setupAxiosInterceptors,
};
